import { ArrowUpRight } from 'lucide-react';
import clsx from 'clsx';
import Reveal from '@/components/Reveal';
import posHall from '@/images/screens/pos-hall.webp';
import posMenu from '@/images/screens/pos-menu.webp';
import adminCashier from '@/images/screens/admin-cashier.webp';
import adminAnalytics from '@/images/screens/admin-analytics.webp';
import guestMenu from '@/images/people/menu.webp';
import guestCheckout from '@/images/people/checkout.webp';
import guestOrder from '@/images/people/order.webp';

type Kind = 'tablet' | 'phone';

interface Shot {
  src: string;
  alt: string;
  width: number;
  height: number;
}

interface Story {
  id: string;
  label: string;
  title: string;
  lead: string;
  bullets: { title: string; text: string }[];
  shots: Shot[];
  kind: Kind;
}

const CASHIER: Story = {
  id: 'cashier',
  label: 'Касса · iPad и iPhone',
  title: 'Зал под контролем с первого касания',
  lead: 'Столы, заказы и оплаты на одном экране. Заказ из электронного меню попадает в кассу мгновенно, со звуковым сигналом.',
  bullets: [
    { title: 'План зала и вынос', text: 'Столы, занятость зоны и выручка смены видны сразу.' },
    { title: 'Любая оплата', text: 'Наличные, карта, перевод, QR и смешанная оплата в два касания.' },
    { title: 'Работает в офлайне', text: 'Пропал интернет? Касса продолжает работать, синхронизация пройдёт сама.' },
  ],
  shots: [
    { src: posHall, alt: 'Касса Resstro на iPad: план зала со столами', width: 1760, height: 1230 },
    { src: posMenu, alt: 'Касса Resstro: меню и сборка заказа стола', width: 1760, height: 1230 },
  ],
  kind: 'tablet',
};

const ADMIN: Story = {
  id: 'admin-mobile',
  label: 'Админка · iPhone',
  title: 'Весь бизнес у вас в кармане',
  lead: 'Открытая смена, выручка и метрики в реальном времени. Не нужно быть в зале, чтобы видеть, что происходит.',
  bullets: [
    { title: 'Касса в реальном времени', text: 'Смена, заказы, выручка и средний чек обновляются каждые 5 секунд.' },
    { title: 'Аналитика за период', text: 'Сегодня, 7, 30 и 90 дней с динамикой к прошлому периоду.' },
    { title: 'Контроль на ходу', text: 'Отмены, скидки и возвраты видны сразу, без выгрузки отчётов.' },
  ],
  shots: [
    { src: adminCashier, alt: 'Мобильная админка Resstro: касса в реальном времени', width: 900, height: 1851 },
    { src: adminAnalytics, alt: 'Мобильная админка Resstro: аналитика и метрики', width: 900, height: 1851 },
  ],
  kind: 'phone',
};

const GUEST: Story = {
  id: 'menu',
  label: 'Электронное меню · для гостей',
  title: 'Гость заказывает сам',
  lead: 'QR на столе вместо бумажного меню. Гость собирает заказ, выбирает «с собой» или «за столом» и платит картой или СБП, без официанта.',
  bullets: [
    { title: 'Оплата онлайн', text: 'ЮKassa и СБП, чек по 54-ФЗ, деньги сразу на ваш счёт.' },
    { title: 'Статус заказа', text: 'Принят, готовим, готов: гость видит этапы и получает пуш, когда можно забирать.' },
    { title: 'Под вашим брендом', text: 'Свой домен и логотип, без упоминания Resstro.' },
  ],
  shots: [
    { src: guestMenu, alt: 'Электронное меню Resstro: витрина блюд с ценами и фото', width: 780, height: 1695 },
    { src: guestCheckout, alt: 'Электронное меню Resstro: оформление заказа и оплата', width: 780, height: 1695 },
    { src: guestOrder, alt: 'Электронное меню Resstro: статус заказа и состав', width: 780, height: 1695 },
  ],
  kind: 'phone',
};

export default function Products() {
  return (
    <section id="products" className="section scroll-mt-24">
      <div className="container-site">
        <div className="grid gap-8 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="h2">Три приложения. Одна база данных.</h2>
            </Reveal>
          </div>
          <Reveal delay={80} className="md:col-span-5">
            <p className="lead">
              Касса на&nbsp;iPad, управление с&nbsp;телефона и&nbsp;электронное меню по&nbsp;QR.
              Ниже реальные экраны продукта, не&nbsp;макеты.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 flex flex-col gap-24 sm:mt-24 sm:gap-32">
          <SplitStory story={CASHIER} flip={false} />
          <SplitStory story={ADMIN} flip />
          <StackedStory story={GUEST} />
        </div>
      </div>
    </section>
  );
}

function StoryText({ story }: { story: Story }) {
  return (
    <>
      <p className="text-[13px] font-medium text-ink/50">{story.label}</p>
      <h3 className="mt-3 text-balance text-[clamp(1.9rem,3.2vw,2.75rem)] font-medium leading-[1.06] tracking-tightest text-ink">
        {story.title}
      </h3>
      <p className="lead mt-5">{story.lead}</p>
    </>
  );
}

function StoryBullets({ story }: { story: Story }) {
  return (
    <ul className="divide-y divide-line border-y border-line">
      {story.bullets.map((b) => (
        <li key={b.title} className="py-4">
          <div className="text-[15px] font-medium tracking-tight text-ink">{b.title}</div>
          <p className="mt-1 text-[13.5px] leading-relaxed text-ink/60">{b.text}</p>
        </li>
      ))}
    </ul>
  );
}

function Stage({ story }: { story: Story }) {
  return (
    <div className="stage-light p-4 sm:p-8">
      <div className="flex items-center justify-between pb-4 text-[11px] font-medium uppercase tracking-[0.16em] text-ink/45 sm:pb-6">
        <span>{story.label}</span>
        <span>Реальные экраны</span>
      </div>
      {story.kind === 'tablet' ? <TabletShowcase shots={story.shots} /> : <PhoneShowcase shots={story.shots} />}
    </div>
  );
}

// Текст слева или справа, сцена с экранами напротив.
function SplitStory({ story, flip }: { story: Story; flip: boolean }) {
  return (
    <article id={story.id} className="grid items-center gap-10 scroll-mt-24 lg:grid-cols-12 lg:gap-14">
      <div className={clsx('lg:col-span-5', flip && 'lg:order-2 lg:col-start-8')}>
        <Reveal>
          <StoryText story={story} />
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-8">
            <StoryBullets story={story} />
          </div>
        </Reveal>
      </div>

      <Reveal delay={120} className={clsx('lg:col-span-7', flip && 'lg:order-1 lg:col-start-1')}>
        <Stage story={story} />
      </Reveal>
    </article>
  );
}

// Текст сверху, три телефона на всю ширину: история гостя не повторяет зигзаг.
function StackedStory({ story }: { story: Story }) {
  return (
    <article id={story.id} className="scroll-mt-24">
      <div className="grid gap-8 lg:grid-cols-12 lg:gap-14">
        <Reveal className="lg:col-span-6">
          <StoryText story={story} />
        </Reveal>
        <Reveal delay={100} className="lg:col-span-5 lg:col-start-8 lg:self-end">
          <StoryBullets story={story} />
        </Reveal>
      </div>

      <Reveal delay={120} className="mt-12">
        <Stage story={story} />
      </Reveal>

      <Reveal delay={160}>
        <div className="mt-8 flex justify-center">
          <a href="https://menu.resstro.ru" target="_blank" rel="noopener noreferrer" className="btn-outline px-6 py-3.5">
            Открыть демо-меню вживую
            <span className="btn-icon">
              <ArrowUpRight size={15} strokeWidth={2.2} />
            </span>
          </a>
        </div>
      </Reveal>
    </article>
  );
}

// Ландшафтные скрины iPad: два кадра, второй со сдвигом вниз.
function TabletShowcase({ shots }: { shots: Shot[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
      {shots.map((s, i) => (
        <div
          key={s.src}
          className={clsx('overflow-hidden rounded-2xl bg-white shadow-deep ring-1 ring-ink/10', i % 2 === 1 && 'sm:mt-10')}
        >
          <img
            src={s.src}
            alt={s.alt}
            width={s.width}
            height={s.height}
            className="block w-full"
            loading="lazy"
            decoding="async"
          />
        </div>
      ))}
    </div>
  );
}

// Портретные скрины iPhone в светлых корпусах. Для трёх кадров средний стоит выше.
function PhoneShowcase({ shots }: { shots: Shot[] }) {
  const trio = shots.length === 3;
  return (
    <div className="flex items-start justify-center gap-4 sm:gap-8">
      {shots.map((s, i) => {
        const lowered = trio ? i !== 1 : i % 2 === 1;
        return (
          <div
            key={s.src}
            className={clsx(
              'phone',
              trio ? 'w-[31%] max-w-[240px]' : 'w-[44%] max-w-[250px]',
              lowered && 'mt-8 sm:mt-12',
            )}
          >
            <div className="phone-screen">
              <img
                src={s.src}
                alt={s.alt}
                width={s.width}
                height={s.height}
                className="block w-full"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
